'use client';

import { Locale } from '@core/Entities/Locale/Locale.entity';

import { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import styles from './HeyCarStaticFinance.module.css';
import { Typography } from '@gds/Typography/Typography';
import Button from '@gds/Button/Button';
import { useNavigateToCheckout } from '../../Hooks/useNavigateToCheckout';
import { getFormattedCurrency } from '@core/Utils/Intl/GetFormattedCurrency';
import { VehicleFinance } from '@core/Entities/Vehicle/VehicleFinance.entity';
import { VehiclePricing } from '@core/Entities/Vehicle/VehiclePricing.entity';
import { isHeycarConnectStaticFinanceDealer } from '@core/Utils/Vehicle/IsHeycarConnect';

type Strings = {
  title: string;
  description: string;
  totalDeposit: string;
  month: string;
  totalMileage: string;
  totalDuration: string;
  orderOnline?: string;
};

export const HeyCarStaticFinance = ({
  strings,
  locale,
  vehicle,
  showDetailsOnly,
}: {
  locale: Locale;
  strings: Strings;
  vehicle: Vehicle;
  showDetailsOnly?: boolean;
}) => {
  const hasStaticFinancing = isHeycarConnectStaticFinanceDealer(vehicle);
  const { handleNavigateToCheckoutClick } = useNavigateToCheckout({ locale });

  if (!hasStaticFinancing || !vehicle?.finance) {
    return null;
  }

  if (showDetailsOnly) {
    return (
      <FinanceDetails
        strings={strings}
        locale={locale}
        vehicleFinance={vehicle.finance}
        vehiclePricing={vehicle.pricing}
      />
    );
  }

  return (
    <div className={styles.wrapper} id="financing-calculator">
      <Typography variant={'h5'} className={styles.title}>
        {strings.title}
      </Typography>
      <Typography variant={'body4'}>{strings.description}</Typography>
      <FinanceDetails
        strings={strings}
        locale={locale}
        vehicleFinance={vehicle.finance}
        vehiclePricing={vehicle.pricing}
      >
        <Button className={styles.button} onClick={handleNavigateToCheckoutClick}>
          {strings.orderOnline}
        </Button>
      </FinanceDetails>
    </div>
  );
};

function FinanceDetails({
  strings,
  locale,
  vehicleFinance,
  vehiclePricing,
  children,
}: {
  locale: Locale;
  strings: Strings;
  vehicleFinance: VehicleFinance;
  vehiclePricing: VehiclePricing;
  children?: React.ReactNode;
}) {
  const { deposit, monthlyPrice, months, mileage } = getFormattedFinanceValuesFromVehicle(
    vehicleFinance,
    vehiclePricing,
    locale,
  );
  return (
    <div className={styles.content}>
      <div className={styles.contentInner}>
        <div>
          <Typography variant={'h5'} Component="p" className={styles.pricePerMonth}>
            {monthlyPrice}/{strings.month}
          </Typography>
          <ul className={styles.breakdown}>
            <Typography variant={'body4'} Component={'li'}>
              {strings.totalDeposit}: <strong>{deposit}</strong>
            </Typography>
            <Typography variant={'body4'} Component={'li'}>
              {strings.totalDuration}:{' '}
              <strong>
                {months} {strings.month}
              </strong>
            </Typography>
            <Typography variant={'body4'} Component={'li'}>
              {strings.totalMileage}: <strong>{mileage}km</strong>
            </Typography>
          </ul>
        </div>
        {children}
      </div>
    </div>
  );
}

function getFormattedFinanceValuesFromVehicle(
  vehicleFinance: VehicleFinance,
  vehiclePricing: VehiclePricing,
  locale: Locale,
) {
  const monthlyPrice = locale !== 'de' ? Math.ceil(vehicleFinance.rate) : vehicleFinance.rate;
  const formattedDeposit = getFormattedCurrency(vehicleFinance.deposit, locale);
  const formattedTotalPrice = getFormattedCurrency(vehiclePricing.price, locale);
  const formattedMonthlyPrice = getFormattedCurrency(monthlyPrice, locale);
  const formattedFinalRate = getFormattedCurrency(vehicleFinance.finalRate, locale);

  return {
    deposit: formattedDeposit,
    totalPrice: formattedTotalPrice,
    monthlyPrice: formattedMonthlyPrice,
    finalRate: formattedFinalRate,
    months: vehicleFinance.months || 0,
    mileage: vehicleFinance.mileage || 0,
  };
}
